import { pipe } from 'fp-ts/function';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { DataTestId, TextKey } from '@mews-commander/enums';
import { ApplicationState, CommanderThunkDispatch, WithGuideLink } from '@mews-commander/types';
import { Card, LocalizedParagraph, FieldsContainer } from '@mews-commander/ui';
import { Banner, Link, Body, Title, Container } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { Target, UnicodeEntity } from '@mews/enums';
import {
    commanderForm,
    maxInputTextLength,
    presence,
    SelectField,
    TextareaField,
    validation,
} from '@mews-commander/legacy-form';
import { isAnythingSelected } from '../../ducks/billItems';
import { changeRebateFormValues, createRebate } from '../actions/form';
import { fieldNames } from '../constants';
import { getRebateReasonsOptions } from '../selectors/rebateReasons';
import {
    RebateFormDispatchProps,
    RebateFormOwnProps,
    RebateFormProps,
    RebateFormStateProps,
    RebateFormValues,
} from '../types';

const ConfirmationDialogContent = ({ guideLink }: WithGuideLink) => (
    <>
        <LocalizedParagraph textKey={TextKey.RebateItemsConfirmationDialogContent} />
        <Body>
            <LocalizedText textKey={TextKey.RebateItemsConfirmationDialogLearnMore} />
            {UnicodeEntity.NoBreakSpace}
            <Link target={Target.Blank} href={guideLink}>
                <LocalizedText textKey={TextKey.CorrectingClosedBill} />
            </Link>
        </Body>
    </>
);
const RebateReasonForm = ({ rebateReasonsOptions }: RebateFormProps) => (
    <Card padding="300">
        <FieldsContainer>
            <Title level={3}>
                <LocalizedText textKey={TextKey.RebateReason} />
            </Title>
            <SelectField
                label={<LocalizedText textKey={TextKey.ChooseFromTheMostCommon} />}
                showEmptyValue
                options={rebateReasonsOptions}
                nullText={TextKey.Other}
                name={fieldNames.PRESELECTED_REBATE_REASON}
            />
            <TextareaField
                label={<LocalizedText textKey={TextKey.Reason} />}
                name={fieldNames.REBATE_REASON}
                required
            />
        </FieldsContainer>

        <Container padding={['200', '0']}>
            <Banner variant="info" primaryText={<LocalizedText textKey={TextKey.RebateInfoMessage} />} />
        </Container>
    </Card>
);

const constraints = {
    [fieldNames.REBATE_REASON]: {
        presence,
        length: maxInputTextLength,
    },
};

export default pipe(
    RebateReasonForm,
    commanderForm({
        submitLabel: <LocalizedText textKey={TextKey.Rebate} />,
        submitButtonProps: { variant: 'primary' },
        validate: validation(constraints),
        onChange: (
            values: RebateFormValues,
            dispatch: Dispatch,
            { form }: RebateFormOwnProps,
            prevValues: RebateFormValues
            // @ts-expect-error $TSFixMeError: Argument of type 'ThunkAction<Action<any>, void>' is not assignable to parameter of type 'AnyAction'.
        ) => dispatch(changeRebateFormValues(values, form, prevValues)),
    }),
    connect<RebateFormStateProps, RebateFormDispatchProps, RebateFormOwnProps, ApplicationState>(
        (state, { scope, guideLink }) => ({
            submitButtonProps: {
                disabled: !isAnythingSelected(state, { scope }),
                'data-test-id': DataTestId.RebatesSubmitButton,
            },
            showConfirmationDialog: true,
            confirmationDialogTitle: TextKey.RebateItemsConfirmationDialogTitle,
            confirmationDialogContent: <ConfirmationDialogContent guideLink={guideLink} />,
            confirmationDialogConfirmButtonTextKey: TextKey.Continue,
            confirmationDialogConfirmButtonVariant: 'primary',
            rebateReasonsOptions: getRebateReasonsOptions(state),
        }),
        (dispatch: CommanderThunkDispatch, { scope }) => ({
            // @ts-expect-error $TSFixMeError: Argument of type '{ scope: string; rebateReason?: string | undefined; preselectedRebateReason?: string | undefined; }' i...
            onSubmit: (values) => dispatch(createRebate({ ...values, scope })),
        })
    )
);
